import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {
  Button,
  Container,
  Row,
  Col,
  Form,
  FormControl,
} from 'react-bootstrap';
import {css} from 'aphrodite';
import Bowser from 'bowser';

import {
  login,
  loginError,
  loginResetAlert,
  LOGIN_TYPE_USER_PASS,
  LOGIN_TYPE_NPI,
  LOGIN_TYPE_NPI_KEY,
  LOGIN_TYPE_TOKEN,
} from 'actions/login';

import {
  getError,
  getInProgress,
  getShowErrorAlert,
} from 'selectors/login';
import {getSettings} from 'selectors/settings';
import {getResolvedLoginSettings} from 'services/authentication';

import {getErrorField, createGlobalErrorObj} from 'libs/react-widgets';
import ErrorAlert from 'components/ErrorAlert';
import Loader from 'components/Loader';
import Isi from 'components/Isi';
import Markdown from 'components/Markdown';

import './styles.scss';

export class Login extends Component {
  constructor(props) {
    super(props);
    // Variables for Login & Password inputs
    this.state = {
      userError: null,
      userPassError: null,
    };

    const browser = Bowser.getParser(window.navigator.userAgent);
    this._isEdge = browser.getBrowserName() === 'Microsoft Edge';

    this._handleSubmitNPI = this._handleSubmitNPI.bind(this);
    this._handleSubmitNPIKey = this._handleSubmitNPIKey.bind(this);
    this._handleSubmitUserPass = this._handleSubmitUserPass.bind(this);
  }

  componentDidMount() {
    const {
      dispatchLogin,
      settings,
    } = this.props;
    if (settings.loginType === LOGIN_TYPE_TOKEN) {
      const loginToken = new URLSearchParams(window.location.search).get('token');
      const loginSettings = getResolvedLoginSettings(settings);
      if (loginToken !== null) {
        dispatchLogin(
          LOGIN_TYPE_TOKEN,
          loginToken,
          settings.name,
          loginSettings.error.apiUnavailable,
          loginSettings.error.userPassIncorrect
        );
      }
    }
  }

  _handleSubmitNPI(e) {
    e.preventDefault();

    const {
      dispatchLogin,
      dispatchLoginError,
      settings,
    } = this.props;
    const loginSettings = getResolvedLoginSettings(settings);

    const npi = e.target.querySelector('input[name="npiNumber"]').value.trim();
    if (npi.match(/^\d{10}$/)) {
      dispatchLogin(
        LOGIN_TYPE_NPI,
        npi,
        loginSettings.error.apiUnavailable,
        loginSettings.error.npiIncorrect
      );
    } else {
      dispatchLoginError({response: createGlobalErrorObj([loginSettings.error.badNpi])});
    }

    this.forceUpdate();
  }

  /**
   * Verifies the npi credentials against a key.
   *
   * @param {tuple}  compareScheme - Dictates how to verify the key
   *                   against the npi number. Contains the
   *                   [compareType, value/regExpression]
   *                   possible compareTypes are "string" and  "regexOfNpiNumber".
   *                   example: ['regexOfNpiNumber', '[13579]']
   *                            ['string', 'password']
   * @param {string} npiNumber - The NPI Number.
   * @param {string} npiKey - The key (aka password).
   *
   * @return {boolean}
   **/
  _verifyNpiKey(compareScheme, npiNumber, npiKey) {
    let [scheme, schemeData] = compareScheme;
    if (scheme === 'string') {
      return (schemeData == npiKey);
    } else if (scheme === 'regexOfNpiNumber') {
      // Compare against a value derived from the npiNumber.
      const matchAgainst = npiNumber.match(new RegExp(schemeData, 'g'));
      if (matchAgainst === null) {
        return false;
      }
      // join() is called because the "password" is a concatenation of all the matches.
      return (matchAgainst.join('') === npiKey);
    } else {
      return false;
    }
  }

  _handleSubmitNPIKey(e) {
    e.preventDefault();

    const {
      dispatchLogin,
      dispatchLoginError,
      settings,
    } = this.props;

    const loginSettings = getResolvedLoginSettings(settings);
    const npi = e.target.querySelector('input[name="npiNumber"]').value.trim();
    const npiKey = e.target.querySelector('input[name="npiKey"]').value.trim();

    // compareType, value/regExpression.
    const compareSchemes = {
      1: ['regexOfNpiNumber', '\\d{4}$'], // Last 4 digits of NPI number.
      2: ['regexOfNpiNumber', '[13579]'], // Odd Digits of NPI number.
      3: ['string', settings.drugId] // productId.
    };

    let keyMatch = false;
    const scheme = compareSchemes[loginSettings.npiKeyScheme];
    if (scheme) {
      keyMatch = this._verifyNpiKey(scheme, npi, npiKey);
    }

    if (npi.match(/^\d{10}$/) && keyMatch) {
      dispatchLogin(
        LOGIN_TYPE_NPI_KEY,
        npi,
        loginSettings.error.apiUnavailable,
        loginSettings.error.npiIncorrect
      );
    } else {
      dispatchLoginError({response: createGlobalErrorObj([loginSettings.error.badNpi])});
    }

    this.forceUpdate();
  }

  _handleSubmitUserPass(e) {
    e.preventDefault();

    const {
      dispatchLogin,
      dispatchLoginError,
      settings,
    } = this.props;

    // Reset Error validation styles.
    let userError = null;
    let userPassError = null;

    const userName = e.target.querySelector('input[name="userLogin"]').value.trim();
    const userPassword = e.target.querySelector('input[name="userPassword"]').value.trim();

    const userRegex = /^[^\s]+$/; // Check username for no spaces.
    const userPasswordRegex = /^[^\s]{8,}$/; // Check for minimum 8 length without any spaces.

    if (!userName.match(userRegex)) {
      userError = 'error';
    }

    if (!userPassword.match(userPasswordRegex)) {
      userPassError = 'error';
    }

    const loginSettings = getResolvedLoginSettings(settings);
    if (!userError && !userPassError) {
      dispatchLogin(LOGIN_TYPE_USER_PASS, userName, userPassword, settings.name, loginSettings.error.apiUnavailable, loginSettings.error.userPassIncorrect);
    } else {
      dispatchLoginError({response: createGlobalErrorObj([loginSettings.error.failed])});
    }

    this.setState({
      userError: userError,
      userPassError: userPassError,
    });

    this.forceUpdate();
  }

  /**
   * Return Login instruction element
   * @returns {JSX.Element}
   */
  loginInstruction() {
    const {
      settings,
      styleSheet,
    } = this.props;
    const loginSettings = getResolvedLoginSettings(settings);
    if (!loginSettings.instruction || !loginSettings.instruction.markdown) {
      return null;
    }

    return (
      <div className={`${css(styleSheet.loginInstruction)} p-0`}>
        <Markdown>
          {loginSettings.instruction.markdown}
        </Markdown>
      </div>
    );
  }

  npiLogin() {
    const {
      dispatchLoginResetAlert,
      error,
      inProgress,
      settings,
      styleSheet,
      showErrorAlert,
    } = this.props;
    const loginSettings = getResolvedLoginSettings(settings);

    return (
      <main id="main-content" data-component={'Login'}>
        <Loader isFetching={inProgress} isFullscreen/>
        <Container>
          <Row className={css(styleSheet.loginBackground)}>
            {loginSettings.instruction && this.loginInstruction()}
            <ErrorAlert error={getErrorField('global', 'global', error)} alertVisible={showErrorAlert} handleDismissAlert={() => dispatchLoginResetAlert()}/>
            <Col sm={4} className={'col-sm-4 mx-auto'}>
              <Form onSubmit={this._handleSubmitNPI}>
                <Form.Group className='mb-3'>
                  <Form.Label htmlFor='npiNumber' className='fw-bold'>NPI Number</Form.Label>
                  <FormControl
                    id={'npiNumber'}
                    name={'npiNumber'}
                    type={'text'}
                    required={'required'}
                    placeholder={'NPI Number'}
                    autoComplete={this._isEdge ? 'off' : 'on'}
                    isInvalid={!!error}
                  />
                </Form.Group>
                <Button type={'submit'} className={`${css(styleSheet.buttonPrimary, styleSheet.loginButton)} w-100`}>{loginSettings.buttonLabel || 'Login'}</Button>
              </Form>
            </Col>
          </Row>
          <div className={css(styleSheet.loginContent)}>
            {loginSettings.content.markdown && <Markdown>{loginSettings.content.markdown}</Markdown>
            }
          </div>
          <Isi settings={settings.isi} styleSheet={styleSheet} />
          {settings.content.bottomSection &&
            <Row><div className={css(styleSheet.bottomSection)}>
              <Markdown>
                {settings.content.bottomSection.markdown}
              </Markdown>
            </div></Row>
          }
        </Container>
      </main>
    );
  }

  npiKeyLogin() {
    const {
      dispatchLoginResetAlert,
      error,
      inProgress,
      settings,
      styleSheet,
      showErrorAlert,
    } = this.props;
    const loginSettings = getResolvedLoginSettings(settings);

    return (
      <main id="main-content" data-component={'Login'}>
        <Loader isFetching={inProgress} isFullscreen/>
        <Container>
          <Row className={css(styleSheet.loginBackground)}>
            {loginSettings.instruction && this.loginInstruction()}
            <ErrorAlert error={getErrorField('global', 'global', error)} alertVisible={showErrorAlert} handleDismissAlert={() => dispatchLoginResetAlert()}/>
            <Col sm={4} className={'col-12 m-auto'}>
              <Form onSubmit={this._handleSubmitNPIKey}>
                <Form.Group className='mb-3'>
                  <Form.Label htmlFor='npiNumber' className={`fw-bold${!!error ? ' text-danger' : ''}`}>{loginSettings.loginFieldLabel}</Form.Label>
                  <FormControl
                    id={'npiNumber'}
                    name={'npiNumber'}
                    type={'text'}
                    required={'required'}
                    placeholder={loginSettings.loginFieldPlaceholder}
                    autoComplete={this._isEdge ? 'off' : 'on'}
                    isInvalid={!!error}
                  />
                  <br/>
                  <Form.Label htmlFor='npiKey' className={`fw-bold${!!error ? ' text-danger' : ''}`}>{loginSettings.passwordFieldLabel}</Form.Label>
                  <FormControl
                    id={'npiKey'}
                    name={'npiKey'}
                    type={'text'}
                    required={'required'}
                    placeholder={loginSettings.passwordFieldPlaceholder}
                    autoComplete={this._isEdge ? 'off' : 'on'}
                    isInvalid={!!error}
                  />
                </Form.Group>
                <Button type={'submit'} className={`${css(styleSheet.buttonPrimary, styleSheet.loginButton)} w-100`}>{loginSettings.buttonLabel || 'Login'}</Button>
              </Form>
            </Col>
          </Row>
          <div className={css(styleSheet.loginContent)}>
            {loginSettings.content.markdown &&
              <Markdown>
                {loginSettings.content.markdown}
              </Markdown>
            }
          </div>
          <Isi settings={settings.isi} styleSheet={styleSheet} />
          {settings.content.bottomSection &&
            <div className={css(styleSheet.bottomSection)}>
              <Markdown>
                {settings.content.bottomSection.markdown}
              </Markdown>
            </div>
          }
        </Container>
      </main>
    );
  }

  userPassLogin() {
    const {
      dispatchLoginResetAlert,
      error,
      inProgress,
      settings,
      styleSheet,
      showErrorAlert,
    } = this.props;
    const loginSettings = getResolvedLoginSettings(settings);

    return (
      <main id="main-content" data-component={'Login'}>
        <Loader isFetching={inProgress} isFullscreen/>
        <Container>
          <Row className={css(styleSheet.loginBackground)}>
            {this.loginInstruction()}
            <ErrorAlert error={getErrorField('global', 'global', error)} alertVisible={showErrorAlert} handleDismissAlert={() => dispatchLoginResetAlert()}/>
            <Col sm={4} className={'offset-sm-4'}>
              <Form onSubmit={this._handleSubmitUserPass}>
                <Form.Group>
                  <Form.Label htmlFor='userLogin'>Username</Form.Label>
                  <FormControl
                    id={'userLogin'}
                    name={'userLogin'}
                    type={'text'}
                    required={'required'}
                    placeholder={'Email Address or User Name'}
                    autoComplete={this._isEdge ? 'off' : 'on'}
                    isInvalid={!!this.state.userError}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label htmlFor='userPassword'>Password</Form.Label>
                  <FormControl
                    id={'userPassword'}
                    name={'userPassword'}
                    type={'password'}
                    required={'required'}
                    placeholder={'Password'}
                    minLength={8}
                    isInvalid={!!this.state.userPassError}
                  />
                </Form.Group>
                <Button type={'submit'} className={css(styleSheet.buttonPrimary, styleSheet.loginButton)}>Login</Button>
              </Form>
            </Col>
          </Row>
          <div className={css(styleSheet.loginContent)}>
            {loginSettings.content.markdown && <Markdown>{loginSettings.content.markdown}</Markdown>
            }
          </div>
          <Isi settings={settings.isi} styleSheet={styleSheet} />
          {settings.content.bottomSection &&
            <div className={css(styleSheet.bottomSection)}>
              <Markdown>{settings.content.bottomSection.markdown}</Markdown>
            </div>
          }
        </Container>
      </main>
    );
  }

  render() {
    const {
      settings,
    } = this.props;

    if (settings.loginType && settings.loginType === LOGIN_TYPE_USER_PASS) {
      return this.userPassLogin();
    }

    if (settings.loginType && settings.loginType === LOGIN_TYPE_TOKEN) {
      // LOGIN_TYPE_TOKEN login code is actually in ComponentDidMount(),
      // inlining the call to dispatch() here would cause an infinite loop.
      // userPassLogin is the fallback Login if token authentication fails.
      return this.userPassLogin();
    }

    if (settings.loginType && settings.loginType === LOGIN_TYPE_NPI_KEY) {
      return this.npiKeyLogin();
    }

    // Return the default NPI login mechanism.
    return this.npiLogin();
  }
}


Login.propTypes = {
  dispatchLogin: PropTypes.func.isRequired,
  dispatchLoginError: PropTypes.func.isRequired,
  dispatchLoginResetAlert: PropTypes.func.isRequired,
  error: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.bool,
  ]),
  showErrorAlert: PropTypes.bool,
  inProgress: PropTypes.bool,
  settings: PropTypes.object.isRequired,
  styleSheet: PropTypes.object.isRequired,
};

const mapStateToProps = function (state) {
  return {
    error: getError(state),
    inProgress: getInProgress(state),
    settings: getSettings(state),
    showErrorAlert: getShowErrorAlert(state),
  };
};

const mapDispatchToProps = function (dispatch) {
  return {
    dispatchLogin: (...args) => {
      dispatch(login(...args));
    },
    dispatchLoginError: (...args) => {
      dispatch(loginError(...args));
    },
    dispatchLoginResetAlert: (...args) => {
      dispatch(loginResetAlert(...args));
    },
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Login);
